import { Swiper } from "swiper";
import { Controller, Parallax, Navigation } from "swiper/modules";
import gsap from "gsap";

class Gallery {
  constructor($el) {
    this.$el = $el;
    this.$image = this.$el.find('[data-gallery="image"]');
    this.$text = this.$el.find('[data-gallery="text"]');

    this.$current = this.$el.find('.gallery__current');
    this.$total = this.$el.find('.gallery__total');
    this.$next = this.$el.find('.gallery__next');
    this.$prev = this.$el.find('.gallery__back');

    this.init();
  }

  init = () => {
    const $slides = this.$text.find(".swiper-slide");
    const totalSlides = $slides.length;

    this.$total.text(this.formatNumber(totalSlides));

    this.imageSwiper = new Swiper(this.$image[0], {
      modules: [Controller, Parallax],
      speed: 1000,
      slidesPerView: 1,
      parallax: true,
      watchSlidesProgress: true,
      loop: false,
      slidesPerGroup: 1,
      longSwipes: false,
      shortSwipes: true,
      controller: {
        // "slide": eigener, vollständiger slideTo()-Übergang mit eigener
        // speed (1000ms) statt proportional/instant der speed:0 des
        // textSwiper zu folgen (Default "container" würde sonst dazu
        // führen, dass der Bildwechsel genauso instant wirkt wie der Text).
        by: "slide",
      },
    });

    this.textSwiper = new Swiper(this.$text[0], {
      modules: [Controller, Navigation],
      speed: 0, // Swipers eigene visuelle Transition ist deaktiviert (siehe CSS),
                // wir übernehmen die komplette Optik selbst per GSAP
      slidesPerView: 1,
      loop: false,
      slidesPerGroup: 1,
      longSwipes: false,
      shortSwipes: true,
      navigation: {
        nextEl: this.$next[0],
        prevEl: this.$prev[0],
      },
      on: {
        init: (swiper) => {
          // Initialzustand: aktiver Slide sichtbar, alle anderen leicht unten "geparkt"
          swiper.slides.forEach((slideEl) => {
            if (slideEl.classList.contains("swiper-slide-active")) {
              gsap.set(slideEl, { y: 0, opacity: 1 });
              slideEl.style.pointerEvents = "auto";
            } else {
              gsap.set(slideEl, { y: 24, opacity: 0 });
              slideEl.style.pointerEvents = "none";
            }
          });

          // Eigenes Tracking des zuletzt tatsächlich angezeigten Slides.
          // Wichtig: NICHT auf swiper.previousIndex verlassen - durch die
          // bidirektionale Controller-Kopplung (imageSwiper <-> textSwiper)
          // kann slideChange mehrfach/widersprüchlich hintereinander feuern.
          this.currentSlideIndex = swiper.activeIndex;
        },
      },
    });

    this.imageSwiper.controller.control = this.textSwiper;
    this.textSwiper.controller.control = this.imageSwiper;

    this.textSwiper.on("slideChange", () => {
      cancelAnimationFrame(this._pendingTransitionFrame);
      // Mehrere slideChange-Events, die durch die Controller-Kopplung
      // innerhalb desselben Frames feuern, auf EINEN Durchlauf bündeln.
      this._pendingTransitionFrame = requestAnimationFrame(() => {
        this.transition();
        this.updateCounter();
      });
    });

    this.updateCounter();
  };

  transition = () => {
    const targetIndex = this.textSwiper.activeIndex;

    if (targetIndex === this.currentSlideIndex) return; // Phantom-Event, kein echter Wechsel

    const outgoingEl = this.textSwiper.slides[this.currentSlideIndex];
    const incomingEl = this.textSwiper.slides[targetIndex];

    this.currentSlideIndex = targetIndex;

    if (!outgoingEl || !incomingEl || outgoingEl === incomingEl) return;

    gsap.killTweensOf([outgoingEl, incomingEl]);

    incomingEl.style.pointerEvents = "auto";

    // aktuellen Text leicht nach oben ausblenden
    gsap.to(outgoingEl, {
      y: -24,
      opacity: 0,
      duration: 0.5,
      ease: "power2.out",
      onComplete: () => {
        outgoingEl.style.pointerEvents = "none";
        // zurücksetzen, damit der Slide beim nächsten Mal wieder von unten reinkommt
        gsap.set(outgoingEl, { y: 24, opacity: 0 });
      },
    });

    // neuen Text leicht von unten einblenden
    gsap.fromTo(
      incomingEl,
      { y: 24, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: "power2.out",
        delay: 0.05,
      }
    );
  };

  updateCounter = () => {
    const index = this.textSwiper.realIndex + 1;
    this.$current.text(this.formatNumber(index));
  };

  formatNumber = (num) => {
    return String(num).padStart(1);
  };
}

export default Gallery;